// TODO (PPC-461: PitchVisualisation - Composition Pattern)
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { isMappedMetricPendingAtom } from '@/atoms/metric/metricFilterMapping';
import { userHasVideoAccessAtom } from '@/atoms/user';
import { ColumnControlsWrapper } from '@/components/controls/ColumnControlsWrapper';
import { VisControls } from '@/components/controls/VisControls';
import { ExportModal } from '@/components/export/ExportModal';
import { InfoBarWithChild } from '@/components/feedback/InfoBar';
import { ThreeSixtyDataUnavailableInfoBar } from '@/components/feedback/ThreeSixtyDataUnavailableInfoBar';
import { TextLink } from '@/components/navigation/Link';
import { OnboardingSettingsButton } from '@/components/onboarding/OnboardingSettingsButton';
import { CONTACT_SUPPORT } from '@/consts/links';
import { useVisDisplayMode } from '@/hooks/useVisDisplayMode';
import { OnboardingItemKey } from '@/types/userConfigs';
import { VisualisationMap } from '@/types/visualisation';
import { VisualisationComponent } from './VisualisationComponent';

const PitchVisualisationWrapper = ({
  headerComponent,
  visualisations,
  footerComponent,
}: {
  headerComponent?: JSX.Element;
  footerComponent?: JSX.Element;
  visualisations: VisualisationMap;
}) => {
  const [visDisplayMode] = useVisDisplayMode();
  const userHasVideoAccess = useAtomValue(userHasVideoAccessAtom);
  const isMappingPending = useAtomValue(isMappedMetricPendingAtom);
  const { t } = useTranslation('onboarding');

  const pitchVisualisation = (
    <VisualisationComponent allowInteraction={userHasVideoAccess} visualisations={visualisations} />
  );

  return (
    <>
      {visDisplayMode === 'both' && (
        <ColumnControlsWrapper headerComponent={headerComponent}>
          <VisControls
            settingsButton={
              <OnboardingSettingsButton
                itemKey={OnboardingItemKey.PITCH_VIS_OBV_COLOURING}
                header={t('pitchVisObvColouring.header')}
                content={t('pitchVisObvColouring.content')}
              />
            }
          />
        </ColumnControlsWrapper>
      )}
      <div className="px-4 pt-4">
        <ThreeSixtyDataUnavailableInfoBar />
        {!userHasVideoAccess && (
          <InfoBarWithChild testId="video-access">
            <Trans i18nKey="upgradeLicence" ns="video">
              Video subscription required. To upgrade,{' '}
              <TextLink to={CONTACT_SUPPORT} target="_blank">
                contact support
              </TextLink>
              .
            </Trans>
          </InfoBarWithChild>
        )}
      </div>
      {pitchVisualisation}
      <div className="mx-auto max-w-[1000px]">{isMappingPending ? null : footerComponent}</div>
      <ExportModal>{pitchVisualisation}</ExportModal>
    </>
  );
};

export const PitchVisualisation = ({
  headerComponent,
  visualisations,
  footerComponent,
}: {
  headerComponent?: JSX.Element;
  footerComponent?: JSX.Element;
  visualisations: VisualisationMap;
}) => (
  <Suspense>
    <PitchVisualisationWrapper {...{ headerComponent, visualisations, footerComponent }} />
  </Suspense>
);
